import { PbxCdrTerminationReason, type PbxCdrCall, type PbxCdrListRequest } from '@/TcxApi/PbxCdr'

/** What a call-log row says when it shows no duration: a termination reason, or that only the PBX answered. */
export type CdrEndReason = PbxCdrTerminationReason | 'ANSWERED_BY_SYSTEM'

/** The selectable call-type filters in the CDR modal. */
export type CdrFilterType = 'ALL' | 'IN' | 'OUT' | 'UNANSWERED' | 'FAILED' | 'CDRNOTE'

export interface BuildCdrQueryOptions {
	/** Index of the first record to return (0-based). */
	offset: number
	/** Number of records to request (page size). */
	limit: number
	/** Extension to scope the calls to. */
	extension?: string
	/** Free-text filter; omitted from the query when empty. */
	filter?: string
	/** Selected call-type filter. */
	type: CdrFilterType
	/** Start of the range, including time of day. */
	from: Date
	/** End of the range, including time of day. */
	to: Date
}

/**
 * Builds the /pbx/cdr request for a single page. The from/to timestamps are passed through
 * verbatim (the modal owns the full-day defaults), only guarding against an inverted range.
 */
export function buildCdrQuery(o: BuildCdrQueryOptions): PbxCdrListRequest {
	const query: PbxCdrListRequest = { offset: o.offset, limit: o.limit, extension: o.extension, recording: true }

	if (o.filter) { query.filter = o.filter }

	switch (o.type) {
		case 'IN': query.direction = 'IN'; break
		case 'OUT': query.direction = 'OUT'; break
		case 'UNANSWERED': query.direction = 'IN'; query.answeredByCallee = false; break
		case 'FAILED': query.terminatedReason = PbxCdrTerminationReason.FAILED; break
		case 'CDRNOTE': query.cdrNote = true; break
	}

	const from = new Date(o.from)
	let to = new Date(o.to)
	if (to.getTime() < from.getTime()) { to = new Date(from) }
	query.from = from
	query.to = to

	return query
}

/**
 * The end reason a call-log row shows in place of a duration. The PBX stamps `answered` whenever it answers the
 * caller's channel, also to play them music on hold in a busy colleague's personal queue, a voicemail greeting or a
 * menu; only `answeredByCallee` says a person picked up. A caller who gave up there ends with HANGUP like a
 * conversation does, so that case reads ANSWERED_BY_SYSTEM. Any other reason (BUSY, ERROR ...) tells more and is kept.
 */
export function cdrEndReason(call: Pick<PbxCdrCall, 'answered' | 'answeredByCallee' | 'terminatedReason'>): CdrEndReason {
	if (call.terminatedReason === PbxCdrTerminationReason.HANGUP && call.answered && !call.answeredByCallee) { return 'ANSWERED_BY_SYSTEM' }

	return call.terminatedReason
}
